import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BuildingOffice2Icon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UsersIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { PageLoader } from '../../components/common/LoadingSpinner';
import type { Tenant } from '../../types';

// ── API helpers ────────────────────────────────────────────
const tenantsApi = {
  getAll:  ()                          => api.get('/tenants').then((r) => r.data.data as Tenant[]),
  create:  (d: { id: string; name: string; description?: string }) =>
    api.post('/tenants', d).then((r) => r.data.data as Tenant),
  toggle:  (id: string, isActive: boolean) =>
    api.put(`/tenants/${id}`, { isActive }).then((r) => r.data.data as Tenant),
  remove:  (id: string)                => api.delete(`/tenants/${id}`).then((r) => r.data.data),
  getUsers:(id: string)               => api.get(`/tenants/${id}/users`).then((r) => r.data.data),
};

// ── Component ──────────────────────────────────────────────
export default function TenantsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  const { data: tenants, isLoading } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: tenantsApi.getAll,
  });

  const { data: tenantUsers } = useQuery({
    queryKey: ['tenant-users', selectedTenant],
    queryFn: () => tenantsApi.getUsers(selectedTenant!),
    enabled: !!selectedTenant,
  });

  const createMutation = useMutation({
    mutationFn: tenantsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant criado com sucesso!');
      setShowCreate(false);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      tenantsApi.toggle(id, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  const removeMutation = useMutation({
    mutationFn: tenantsApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant removido');
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Erro ao remover tenant';
      toast.error(msg);
    },
  });

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="h-6 w-6" style={{ color: '#00d4ff' }} />
            <h1 className="page-title">Gestão de Tenants</h1>
          </div>
          <p className="page-subtitle">{tenants?.length ?? 0} tenants cadastrados</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <PlusIcon className="h-4 w-4" />
          Novo Tenant
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <span className="stat-label">Total de Tenants</span>
          <span className="stat-value text-white">{tenants?.length ?? 0}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Ativos</span>
          <span className="stat-value text-success-500">
            {tenants?.filter((t) => t.isActive).length ?? 0}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Inativos</span>
          <span className="stat-value text-danger-500">
            {tenants?.filter((t) => !t.isActive).length ?? 0}
          </span>
        </div>
      </div>

      {/* Tabela de tenants */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Descrição</th>
                <th className="text-center">Status</th>
                <th className="text-center">Usuários</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tenants?.map((tenant) => (
                <tr key={tenant.id} className={tenant.id === '00000' ? 'bg-[#00d4ff]/[0.03]' : ''}>
                  <td>
                    <span className="font-mono font-bold text-white/80 bg-white/5 px-2 py-0.5 rounded text-xs">
                      {tenant.id}
                    </span>
                    {tenant.id === '00000' && (
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,212,255,0.1)', color: '#00d4ff' }}>
                        SISTEMA
                      </span>
                    )}
                  </td>
                  <td className="font-medium text-white">{tenant.name}</td>
                  <td className="text-white/40 text-xs max-w-[200px] truncate">
                    {tenant.description ?? '—'}
                  </td>
                  <td className="text-center">
                    {tenant.isActive ? (
                      <span className="badge badge-green">Ativo</span>
                    ) : (
                      <span className="badge badge-red">Inativo</span>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => setSelectedTenant(selectedTenant === tenant.id ? null : tenant.id)}
                      className="flex items-center gap-1 mx-auto text-xs text-white/50 hover:text-white transition-colors"
                    >
                      <UsersIcon className="h-3.5 w-3.5" />
                      <span className="font-mono">{(tenant.userCount as unknown as number) ?? '?'}</span>
                    </button>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {tenant.id !== '00000' && (
                        <>
                          <button
                            onClick={() => toggleMutation.mutate({ id: tenant.id, isActive: !tenant.isActive })}
                            className="btn-ghost btn-sm"
                            title={tenant.isActive ? 'Desativar' : 'Ativar'}
                          >
                            {tenant.isActive
                              ? <XCircleIcon className="h-4 w-4 text-warning-500" />
                              : <CheckCircleIcon className="h-4 w-4 text-success-500" />
                            }
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Remover tenant "${tenant.name}" (${tenant.id})?`)) {
                                removeMutation.mutate(tenant.id);
                              }
                            }}
                            className="btn-ghost btn-sm text-danger-500"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Usuários do tenant selecionado */}
      {selectedTenant && tenantUsers && (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <UsersIcon className="h-4 w-4 text-primary-400" />
            Usuários — Tenant {selectedTenant}
          </h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Role</th>
                  <th>Criado em</th>
                </tr>
              </thead>
              <tbody>
                {(tenantUsers as { id: string; name: string; email: string; role: string; created_at: string }[]).map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium text-white">{u.name}</td>
                    <td className="text-white/60 text-xs">{u.email}</td>
                    <td>
                      <span
                        className={`badge ${u.role === 'superadmin' ? '' : u.role === 'admin' ? 'badge-blue' : 'badge-yellow'}`}
                        style={u.role === 'superadmin' ? { background: 'rgba(0,212,255,0.1)', color: '#00d4ff' } : {}}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="text-white/40 text-xs">
                      {new Date(u.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal criar tenant */}
      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isPending={createMutation.isPending}
        />
      )}
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────
function CreateTenantModal({
  onClose,
  onSubmit,
  isPending,
}: {
  onClose: () => void;
  onSubmit: (data: { id: string; name: string; description?: string }) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({ id: '', name: '', description: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[0-9A-Z]{5}$/.test(form.id)) {
      toast.error('ID deve ter exatamente 5 caracteres alfanuméricos maiúsculos (ex: A1B2C)');
      return;
    }
    onSubmit({ id: form.id, name: form.name, description: form.description || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-800 rounded-2xl border border-white/10 w-full max-w-md shadow-2xl">
        <div className="flex items-center gap-3 p-6 border-b border-white/5">
          <BuildingOffice2Icon className="h-5 w-5" style={{ color: '#00d4ff' }} />
          <h2 className="text-lg font-semibold text-white">Novo Tenant</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">ID do Tenant <span className="text-white/30">(5 chars, ex: A1B2C)</span></label>
            <input
              className="input font-mono uppercase"
              maxLength={5}
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })}
              placeholder="A1B2C"
              required
            />
          </div>
          <div>
            <label className="label">Nome da Empresa</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Empresa ABC Ltda."
              required
            />
          </div>
          <div>
            <label className="label">Descrição <span className="text-white/30">(opcional)</span></label>
            <input
              className="input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Breve descrição do tenant"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={isPending} className="btn-primary flex-1">
              {isPending ? 'Criando...' : 'Criar Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
